import type { RootState } from "store/types";

// `pages` (src/reducers/pages.js) unwound at the selector boundary for containers/Games
// (PLAY-5160). Deliberately *not* converted with `.toJS()`: `lobbyContent` is read by
// `Games` itself (`.getIn([0])` in `_onCloseGameHandler`) AND passed straight through to
// the unconverted `LobbyGameNavigation` as `content` — it stays Immutable and opaque here,
// the same exception `store/selectors/historyLinks.ts` documents for HAL-style route
// objects still consumed by an unconverted child.
export function selectLobbyContent(state: RootState): any {
  try {
    return state.pages
      .get("wl-pages")
      .find((page: any) => page.get("rel") === "webLobbyMain")
      .get("content");
  } catch {
    return null;
  }
}

// Generalizes the `wl-pages` lookup above to an arbitrary `rel` (design.md D6, PLAY-5216 Phase
// 1) — `containers/Categories`, `containers/Content`, and `containers/GameNavigation` each derive
// a different final shape from the same underlying page, so this returns the raw Immutable
// `content` and leaves that derivation to each container's own selector(s), same as
// `selectLobbyContent` already does for the `webLobbyMain` case specifically.
export function selectPageByRel(state: RootState, rel: string | undefined): any {
  try {
    return state.pages
      .get("wl-pages")
      .find((page: any) => page.get("rel") === rel)
      .get("content");
  } catch {
    return undefined;
  }
}

// containers/Navigation (PLAY-5216, Phase 3): mapStateToProps' page derivation ported verbatim,
// unwound to a boolean since Navigation's render only ever checks `page` for truthiness
// (`page && balance`), never reads a field off it.
export function selectHasPage(state: RootState, pageId: string | undefined): boolean {
  try {
    if (pageId === "myQT") {
      return state.pages.get("wl-pages").some((page: any) => page.get("rel") === pageId);
    }
    return !!selectPageByRel(state, "webLobbyMain")?.find((page: any) => page.get("rel") === pageId);
  } catch {
    return false;
  }
}

export function selectHasMyQTPage(state: RootState): boolean {
  try {
    return state.pages.get("wl-pages").some((page: any) => page.get("rel") === "myQT");
  } catch {
    return false;
  }
}

export function selectWebLobbyMainCategoryCount(state: RootState): number | undefined {
  try {
    return selectPageByRel(state, "webLobbyMain")?.size;
  } catch {
    return undefined;
  }
}

// containers/Category (PLAY-5216, Phase 3): mapStateToProps' un-guarded `state.pages.get(...)
// .get("content")` chain ported, but safe against a missing webLobbyMain page or matching content
// entry (the original threw uncaught past `if (state.pages)` when either was absent). Stays
// Immutable and opaque - Category finds its own filters/games links off this with `.get`/`.find`
// and passes both straight through to unconverted children (CategoryFiltersComponent,
// CategoryListItem).
export function selectCategoryLinks(state: RootState, pageId: string | undefined): any {
  try {
    const content = selectPageByRel(state, "webLobbyMain")?.find((page: any) => page.get("rel") === pageId);
    return content?.get("links");
  } catch {
    return undefined;
  }
}

// containers/Application (PLAY-5216, Phase 4): the raw `pages` slice itself, not narrowed to any
// one page - Application checks it for presence (scrollLock) and walks `wl-pages` itself to
// resolve a default lobby location. Stays Immutable and opaque, same convention as this file's
// other selectors.
export function selectPagesState(state: RootState): any {
  return state.pages;
}
